// src/App.js
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';

import RequireAuth      from './RequireAuth';
import useSidebarCollapsed from './useSidebarCollapsed';
import { watchForPhoto } from './microsoftPhoto';
import Screen           from './Screen';
import Header           from './Header';
import Sidebar          from './Sidebar';
import DashboardMetrics from './DashboardMetrics';
import Reports          from './Reports';
import ContactUsPage    from './ContactUsPage';
import MarketingPage    from './MarketingPage';
import CouponsPage      from './CouponsPage';
import DisputesPage     from './DisputesPage';
import ShoppingBudgetsPage from './ShoppingBudgetsPage';
import BookingsPage     from './BookingsPage';
import GenericTablePage from './GenericTablePage';
import MicrosoftLogin   from './MicrosoftLogin';
import ProfilePage      from './ProfilePage';
import ProfilesPage     from './ProfilesPage';
import ChatsPage        from './ChatsPage';
import PayoutsPage      from './PayoutsPage';
import PaymentsPage     from './PaymentsPage';
import ClosetPage       from './ClosetPage';
import BlockedUsersPage from './BlockedUsersPage';
import ActivityLogsPage from './ActivityLogsPage';
import SystemSettingsPage from './SystemSettingsPage';
import AuditChecklistPage from './AuditChecklistPage';
import IssuesPage from './IssuesPage';
import AdminUsersPage from './AdminUsersPage';
import SubscriptionsPage from './SubscriptionsPage';
import AnalyticsDashboard from './AnalyticsDashboard';
import BusinessMetrics from './BusinessMetrics';
import ReviewsPage from './ReviewsPage';
import ReportsQueuePage from './ReportsQueuePage';
import LegalCompliancePage from './LegalCompliancePage';
import StylistApprovalPage from './StylistApprovalPage';
import PulsePage from './PulsePage';

import navItems from './navItems';
import { Box } from '@mui/material';

import { surface } from './designTokens';
// Initialize Google Analytics if configured
if (process.env.REACT_APP_GA4_PROPERTY_ID) {
  ReactGA.initialize(process.env.REACT_APP_GA4_PROPERTY_ID);
}

const AppContent = () => {
  const location = useLocation();
  // The header sits beside the sidebar, so its margin has to follow it. This
  // was hardcoded to 240px and would have left a gap the width of a sidebar
  // that is no longer there.
  const { width: sidebarWidth } = useSidebarCollapsed();
  const [activeTab, setActiveTab] = useState('Home');

  // Sync sidebar and send a GA pageview on route change
  useEffect(() => {
    const match = navItems.find(i => i.path === location.pathname);
    if (match) {
      setActiveTab(match.table);
    } else if (location.pathname.startsWith('/dashboard')) {
      setActiveTab('Home');
    }
    ReactGA.send({ hitType: 'pageview', page: location.pathname });
  }, [location]);

  // Get page title and subtitle based on active tab
  const getPageInfo = () => {
    const match = navItems.find(i => i.table === activeTab);
    if (activeTab === 'Home') {
      return {
        title: '',
        subtitle: ''
      };
    }
    const label = match?.label || activeTab;
    return {
      title: label,
      subtitle: `Manage ${label.toLowerCase()}`
    };
  };

  const pageInfo = getPageInfo();
  console.log('App: activeTab:', activeTab, 'pageInfo:', pageInfo);

  const renderContent = () => {
    console.log('App: Rendering content for activeTab:', activeTab);
    switch (activeTab) {
      case 'Home':
        return <DashboardMetrics />;
      case 'Analytics':
        return <AnalyticsDashboard />;
      case 'Pulse':
        return <PulsePage />;
      case 'Business Metrics':
        return <BusinessMetrics />;
      case 'Profiles':
        return <ProfilesPage />;
      case 'Reports':
        return <Reports />;
      case 'Contact Us':
        return <ContactUsPage />;
      case 'Marketing':
        return <MarketingPage />;
      case 'Coupons':
        return <CouponsPage />;
      case 'Disputes':
        return <DisputesPage />;
      case 'Shopping Budgets':
        return <ShoppingBudgetsPage />;
      case 'bookings':
        return <BookingsPage />;
      case 'Chats':
        return <ChatsPage />;
      case 'Payouts':
        return <PayoutsPage />;
      case 'payments':
        return <PaymentsPage />;
      case 'Closet Items':
        return <ClosetPage />;
      case 'Blocked Users':
        return <BlockedUsersPage />;
      case 'Issues':
        return <IssuesPage />;
      case 'Activity Logs':
        return <ActivityLogsPage />;
      case 'System Settings':
        return <SystemSettingsPage />;
      case 'Audit Checklist':
        return <AuditChecklistPage />;
      case 'User Management':
        return <AdminUsersPage />;
      case 'Subscriptions':
        return <SubscriptionsPage />;
      case 'Reviews':
        return <ReviewsPage />;
      case 'Stylist Approval':
        return <StylistApprovalPage />;
      case 'Reports Queue':
        return <ReportsQueuePage />;
      case 'Legal Compliance':
        return <LegalCompliancePage />;
      default:
        return <GenericTablePage />;
    }
  };

  return (
    <Screen>
      <Box
        sx={{
          ml: `${sidebarWidth}px`,
          width: `calc(100% - ${sidebarWidth}px)`,
          transition: 'margin .2s ease, width .2s ease',
        }}
      >
        <Header pageTitle={pageInfo.title} pageSubtitle={pageInfo.subtitle} />
      </Box>
      <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
        <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: activeTab === 'Home' ? surface.sunken : 'background.default',
            height: '100%',
            overflowY: 'auto',
            p: 0,
          }}
        >
          {renderContent()}
        </Box>
      </Box>
    </Screen>
  );
};

export default function App() {
  // Armed once, at the root. The OAuth redirect lands on /dashboard and
  // supabase-js parses it there, so SIGNED_IN fires wherever the app happens to
  // be rather than on the login screen — and provider_token, the only chance to
  // read the Microsoft photo, exists only on that one event.
  useEffect(() => watchForPhoto(), []);

  return (
    <Routes>
      {/* Public login route */}
      <Route path="/" element={<MicrosoftLogin />} />

      {/* Profile detail (uses :id param) */}
      <Route
        path="/dashboard/profile/:id"
        element={
          <RequireAuth>
            <ProfilePage />
          </RequireAuth>
        }
      />

      {/* All other dashboard routes */}
      <Route
        path="/dashboard/*"
        element={
          <RequireAuth>
            <AppContent />
          </RequireAuth>
        }
      />

      {/* Catch-all → redirect to login */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
